import {useState} from "react"
import {Alert, Button, Card, Col, Form, Row, Table} from "react-bootstrap"

import {layoutDisplayName} from "./display"
import JsonEditor from "./JsonEditor"
import type {
    Configuration,
    Equipment,
    Installation,
    Layout,
    SeatPlan,
} from "./types"

export const RoomInstallationComponent = ({
    installation,
    editing,
    onSave,
}: {
    installation: Installation | null
    editing: boolean
    onSave: (value: Installation) => Promise<void>
}) => installation ? <JsonEditor
    title={`${installation.name} (${installation.outlets.length} Steckdosen)`}
    value={installation}
    onSave={onSave}
    readOnly={!editing}
/> : null

export const RoomSeatPlanDataComponent = ({
    seatPlan,
    editing,
    onSave,
}: {
    seatPlan: SeatPlan | null
    editing: boolean
    onSave: (value: SeatPlan) => Promise<void>
}) => seatPlan ? <JsonEditor
    title={`${seatPlan.name} (${seatPlan.seats.length} Sitze)`}
    value={seatPlan}
    onSave={onSave}
    readOnly={!editing}
/> : null

export const RoomSeatLayoutsComponent = ({
    layouts,
    selectedKey,
    editing,
    onSelect,
    onSave,
    onGenerate,
    onDelete,
}: {
    layouts: Layout[]
    selectedKey: string
    editing: boolean
    onSelect: (key: string) => void
    onSave: (value: Layout) => Promise<void>
    onGenerate: (name: string, minimumFreeSeats: number) => Promise<void>
    onDelete: (layout: Layout) => Promise<void>
}) => {
    const selected = layouts.find(layout => layout.key === selectedKey)
    const [name, setName] = useState("")
    const [minimumFreeSeats, setMinimumFreeSeats] = useState(1)
    const [saving, setSaving] = useState(false)
    const generate = async () => {
        if (!name.trim()) return
        setSaving(true)
        try {
            await onGenerate(name.trim(), minimumFreeSeats)
            setName("")
        } finally {
            setSaving(false)
        }
    }
    return <>
        {editing && <Card className="mb-3">
            <Card.Body>
                <Card.Title>Eigene Sitzplatzaufteilung erzeugen</Card.Title>
                <p className="text-muted">
                    Zwischen belegten Prüfungsreihen bleibt immer eine Reihe frei.
                    Sitze ohne Schreibfläche und barrierefreie Sitze werden nicht
                    verwendet.
                </p>
                <Row className="g-2 align-items-end">
                    <Col md={7}>
                        <Form.Label>Name</Form.Label>
                        <Form.Control value={name} maxLength={120}
                            placeholder="z. B. Zwei Plätze frei"
                            onChange={event => setName(event.target.value)}/>
                    </Col>
                    <Col md={3}>
                        <Form.Label>Freie Plätze dazwischen</Form.Label>
                        <Form.Control type="number" min={0} max={10}
                            value={minimumFreeSeats}
                            onChange={event => setMinimumFreeSeats(
                                Number(event.target.value)
                            )}/>
                    </Col>
                    <Col md={2}>
                        <Button className="w-100" disabled={
                            saving || !name.trim() ||
                            !Number.isInteger(minimumFreeSeats) ||
                            minimumFreeSeats < 0 || minimumFreeSeats > 10
                        } onClick={() => void generate()}>
                            Erzeugen
                        </Button>
                    </Col>
                </Row>
            </Card.Body>
        </Card>}
        <Form.Select className="mb-3" value={selectedKey}
            onChange={event => onSelect(event.target.value)}>
            {!layouts.length && <option value="">Keine Aufteilung vorhanden</option>}
            {layouts.map(layout => <option key={layout.key} value={layout.key}>
                {layoutDisplayName(layout)}
            </option>)}
        </Form.Select>
        {selected && <>
            {selected.generation?.origin === "GENERATED" && <Alert
                variant="info">
                Automatisch erzeugter Entwurf: eine freie Reihe und{" "}
                {selected.generation.minimumFreeSeats} freie Plätze zwischen
                Prüfungsplätzen.
            </Alert>}
            <JsonEditor title={selected.name}
                value={selected} onSave={onSave} readOnly={!editing}/>
            {editing && selected.status === "draft" &&
                selected.generation?.origin === "GENERATED" &&
                <Button variant="outline-danger"
                    onClick={() => void onDelete(selected)}>
                    Generiertes Layout löschen
                </Button>}
        </>}
    </>
}

export const RoomEquipmentComponent = ({
    equipment,
    selectedKey,
    editing,
    onSelect,
    onSave,
}: {
    equipment: Equipment[]
    selectedKey: string
    editing: boolean
    onSelect: (key: string) => void
    onSave: (value: Equipment) => Promise<void>
}) => {
    const selected = equipment.find(item => item.key === selectedKey)
    return <>
        <Form.Select className="mb-3" value={selectedKey}
            onChange={event => onSelect(event.target.value)}>
            {equipment.map(item => <option key={item.key} value={item.key}>
                {item.name}
            </option>)}
        </Form.Select>
        {selected && <JsonEditor title={selected.name}
            value={selected} onSave={onSave} readOnly={!editing}/>}
    </>
}

export const RoomPowerGraphComponent = ({
    configurations,
    selectedKey,
    editing,
    onSelect,
    onSave,
}: {
    configurations: Configuration[]
    selectedKey: string
    editing: boolean
    onSelect: (key: string) => void
    onSave: (value: Configuration) => Promise<void>
}) => {
    const selected = configurations.find(item => item.key === selectedKey)
    return <>
        <Form.Select className="mb-3" value={selectedKey}
            onChange={event => onSelect(event.target.value)}>
            {configurations.map(item => <option
                key={`${item.key}-${item.revision}`} value={item.key}>
                {item.name}
            </option>)}
        </Form.Select>
        {selected && <>
            {selected.calculated?.validationErrors?.map(error =>
                <Alert variant="warning" key={error}>{error}</Alert>)}
            <Card className="mb-3">
                <Card.Body>
                    <Table responsive size="sm">
                        <thead><tr>
                            <th>Knoten</th>
                            <th>Kanten</th>
                            <th>Versorgte Plätze</th>
                            <th>Kettentiefe</th>
                        </tr></thead>
                        <tbody><tr>
                            <td>{selected.nodes.length}</td>
                            <td>{selected.edges.length}</td>
                            <td>{selected.calculated?.poweredExamSeats}</td>
                            <td>{selected.calculated?.maximumChainDepth}</td>
                        </tr></tbody>
                    </Table>
                </Card.Body>
            </Card>
            <JsonEditor title={selected.name} value={selected}
                onSave={onSave} readOnly={!editing}/>
        </>}
    </>
}
